import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'
import {
  ArrowLeft,
  Ban,
  CalendarPlus,
  CheckCircle2,
  Clock,
  Plus,
  XCircle,
} from 'lucide-react'

import { VACATION_PAYMENT_DISCLAIMER } from '@czas/shared'

import Button from '@/components/ui/Button'
import Header from '@/components/layout/Header'
import FullScreenSpinner from '@/components/layout/FullScreenSpinner'
import Alert from '@/components/ui/Alert'
import { apiErrorMessage } from '@/lib/api'
import { useSession } from '@/lib/auth'
import { cn } from '@/lib/cn'

import PaymentBadge from '@/features/vacation/payment-badge'
import VacationRequestDetails, {
  ExpandToggle,
  formatSubmittedShort,
} from '@/features/vacation/vacation-request-details'
import VacationRequestDialog from '@/features/vacation/vacation-request-dialog'
import {
  useCancelMyVacationRequest,
  useMyVacationBalance,
  useMyVacationRequests,
  type VacationRequest,
  type VacationStatus,
  type VacationType,
} from '@/features/vacation/use-vacation'

const TYPE_LABEL: Record<VacationType, string> = {
  WYPOCZYNKOWY: 'Wypoczynkowy',
  NA_ZADANIE: 'Na żądanie',
  BEZPLATNY: 'Bezpłatny',
  CHOROBOWY: 'Chorobowy (L4)',
  OPIEKA_NAD_DZIECKIEM: 'Opieka nad dzieckiem',
}

const STATUS_LABEL: Record<VacationStatus, string> = {
  PENDING: 'Oczekujący',
  APPROVED: 'Zatwierdzony',
  REJECTED: 'Odrzucony',
  CANCELLED: 'Anulowany',
}

function yearOptions(): number[] {
  const current = new Date().getFullYear()
  const start = current - 1
  const end = current + 3
  const out: number[] = []
  for (let y = start; y <= end; y++) out.push(y)
  return out
}

function todayIso(): string {
  const now = new Date()
  const tzOffset = now.getTimezoneOffset() * 60_000
  return new Date(now.getTime() - tzOffset).toISOString().slice(0, 10)
}

function formatDateRange(start: string, end: string): string {
  const [sy, sm, sd] = start.split('-').map(Number)
  const [ey, em, ed] = end.split('-').map(Number)
  const sDate = new Date(Date.UTC(sy!, sm! - 1, sd!))
  const eDate = new Date(Date.UTC(ey!, em! - 1, ed!))
  if (start === end) {
    return format(sDate, 'd MMMM yyyy', { locale: pl })
  }
  if (sy === ey && sm === em) {
    return `${sd}–${ed} ${format(sDate, 'MMMM yyyy', { locale: pl })}`
  }
  if (sy === ey) {
    return `${format(sDate, 'd MMMM', { locale: pl })} – ${format(eDate, 'd MMMM yyyy', { locale: pl })}`
  }
  return `${format(sDate, 'd MMM yyyy', { locale: pl })} – ${format(eDate, 'd MMM yyyy', { locale: pl })}`
}

function StatusBadge({ status }: { status: VacationStatus }) {
  const map: Record<VacationStatus, { cls: string; Icon: typeof Clock }> = {
    PENDING: { cls: 'bg-warning/10 text-warning border-warning/30', Icon: Clock },
    APPROVED: { cls: 'bg-success/10 text-success border-success/30', Icon: CheckCircle2 },
    REJECTED: { cls: 'bg-danger/10 text-danger border-danger/30', Icon: XCircle },
    CANCELLED: { cls: 'bg-bg-surface text-text-muted border-border-subtle', Icon: Ban },
  }
  const { cls, Icon } = map[status]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-pill border px-2 py-0.5 text-xs font-medium',
        cls,
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {STATUS_LABEL[status]}
    </span>
  )
}

export default function VacationPage() {
  const session = useSession()
  const showOpieka = session.data?.hasChildrenUnder14 === true
  const [year, setYear] = useState(() => new Date().getFullYear())
  const [dialogOpen, setDialogOpen] = useState(false)
  const [toCancel, setToCancel] = useState<VacationRequest | null>(null)

  const balanceQuery = useMyVacationBalance(year)
  const requestsQuery = useMyVacationRequests({ year })

  if (balanceQuery.isLoading && requestsQuery.isLoading) {
    return <FullScreenSpinner />
  }

  return (
    <div className="min-h-[100svh] flex flex-col bg-bg-canvas">
      <Header />
      <main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <div className="mb-4">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Powrót do pracy
          </Link>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="text-2xl font-semibold text-text-primary">Mój urlop</h1>
          <div className="flex items-center gap-2">
            <label htmlFor="vacation-year" className="text-sm text-text-muted">
              Rok:
            </label>
            <select
              id="vacation-year"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 rounded-action border border-border-strong bg-bg-elevated px-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
            >
              {yearOptions().map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>

        {balanceQuery.error && (
          <Alert variant="error">{apiErrorMessage(balanceQuery.error)}</Alert>
        )}

        {balanceQuery.data && (
          <>
            <BalanceCard balance={balanceQuery.data.balance} />
            <ByTypeCard
              byType={balanceQuery.data.byType}
              showOpieka={showOpieka}
            />
          </>
        )}

        <div className="mt-8 flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-text-primary">Moje wnioski</h2>
          <Button type="button" onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            Złóż wniosek
          </Button>
        </div>

        {requestsQuery.isLoading && (
          <p className="text-sm text-text-muted py-4 text-center">Ładuję…</p>
        )}
        {requestsQuery.data && (
          <RequestsList
            requests={requestsQuery.data.requests}
            onCancel={setToCancel}
          />
        )}

        <p className="text-[11px] text-text-faint text-center mt-6">
          {VACATION_PAYMENT_DISCLAIMER}
        </p>
      </main>

      <VacationRequestDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />

      {toCancel && (
        <CancelConfirmDialog
          request={toCancel}
          onClose={() => setToCancel(null)}
        />
      )}
    </div>
  )
}

function BalanceCard({
  balance,
}: {
  balance: { totalDays: number; usedDays: number; pendingDays: number; remainingDays: number }
}) {
  return (
    <section className="rounded-card border border-border-subtle bg-bg-elevated p-4">
      <div className="text-xs uppercase tracking-wider text-text-faint mb-2">
        Bilans wypoczynkowy
      </div>
      <div className="grid grid-cols-4 gap-2">
        <BalanceMetric label="Razem" value={balance.totalDays} />
        <BalanceMetric label="Wykorzystane" value={balance.usedDays} />
        <BalanceMetric label="Oczekujące" value={balance.pendingDays} />
        <BalanceMetric label="Pozostałe" value={balance.remainingDays} accent />
      </div>
    </section>
  )
}

function BalanceMetric({
  label,
  value,
  accent,
}: {
  label: string
  value: number
  accent?: boolean
}) {
  return (
    <div className="text-center">
      <div
        className={cn(
          'font-mono text-3xl font-semibold tabular-nums',
          accent ? 'text-brand-accent' : 'text-text-primary',
        )}
      >
        {value}
      </div>
      <div className="text-xs text-text-faint mt-0.5">{label}</div>
    </div>
  )
}

function ByTypeCard({
  byType,
  showOpieka,
}: {
  byType: import('@/features/vacation/use-vacation').VacationBalanceByType
  showOpieka: boolean
}) {
  return (
    <section className="rounded-card border border-border-subtle bg-bg-elevated p-4 mt-3 text-sm">
      <div className="text-xs uppercase tracking-wider text-text-faint mb-2">
        Szczegóły wg typu
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
        <Row
          type="WYPOCZYNKOWY"
          label="Wypoczynkowy"
          value={`${byType.WYPOCZYNKOWY.approvedDays} dni`}
          extra={byType.WYPOCZYNKOWY.pendingDays > 0 ? `+ ${byType.WYPOCZYNKOWY.pendingDays} w oczekiwaniu` : null}
        />
        <Row
          type="NA_ZADANIE"
          label="Na żądanie"
          value={`${byType.NA_ZADANIE.approvedDays} / ${byType.NA_ZADANIE.maxPerYear} dni`}
          extra={byType.NA_ZADANIE.pendingDays > 0 ? `+ ${byType.NA_ZADANIE.pendingDays} w oczekiwaniu` : null}
        />
        <Row type="BEZPLATNY" label="Bezpłatny" value={`${byType.BEZPLATNY.approvedDays} dni`} />
        <Row type="CHOROBOWY" label="Chorobowy (L4)" value={`${byType.CHOROBOWY.approvedDays} dni`} />
        {showOpieka && (
          <Row
            type="OPIEKA_NAD_DZIECKIEM"
            label="Opieka nad dzieckiem"
            value={`${byType.OPIEKA_NAD_DZIECKIEM.approvedDays} / ${byType.OPIEKA_NAD_DZIECKIEM.maxPerYear} dni`}
            extra={byType.OPIEKA_NAD_DZIECKIEM.pendingDays > 0 ? `+ ${byType.OPIEKA_NAD_DZIECKIEM.pendingDays} w oczekiwaniu` : null}
          />
        )}
      </dl>
    </section>
  )
}

function Row({
  type,
  label,
  value,
  extra,
}: {
  type: VacationType
  label: string
  value: string
  extra?: string | null
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-text-muted inline-flex items-center gap-1.5">
        {label}
        <PaymentBadge type={type} />:
      </dt>
      <dd className="text-text-primary">
        <span className="font-medium">{value}</span>
        {extra && <span className="text-text-faint ml-1">({extra})</span>}
      </dd>
    </div>
  )
}

function RequestsList({
  requests,
  onCancel,
}: {
  requests: VacationRequest[]
  onCancel: (req: VacationRequest) => void
}) {
  const today = todayIso()
  const [expandedId, setExpandedId] = useState<string | null>(null)

  if (requests.length === 0) {
    return (
      <div className="rounded-card border border-border-subtle bg-bg-elevated p-8 text-center">
        <CalendarPlus className="h-8 w-8 mx-auto text-text-faint mb-2" aria-hidden />
        <p className="text-text-muted text-sm">
          Nie masz żadnych wniosków o urlop. Złóż pierwszy klikając przycisk powyżej.
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-card border border-border-subtle bg-bg-elevated overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bg-surface text-text-muted text-xs uppercase tracking-wider">
            <tr>
              <th className="w-8" aria-label="Szczegóły" />
              <th className="text-left px-3 py-3 font-medium">Status</th>
              <th className="text-left px-3 py-3 font-medium">Typ</th>
              <th className="text-left px-3 py-3 font-medium">Zakres</th>
              {/* Na telefonie data wpłynięcia jedzie pod zakresem — siódma
                  kolumna zmusiłaby do przewijania w bok. */}
              <th className="text-left px-3 py-3 font-medium hidden sm:table-cell">
                Wpłynął
              </th>
              <th className="text-right px-3 py-3 font-medium w-20">Dni</th>
              <th className="text-right px-3 py-3 font-medium w-32">Akcje</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => {
              const canCancel =
                r.status === 'PENDING' ||
                (r.status === 'APPROVED' && r.startDate > today)
              const expanded = expandedId === r.id
              const toggle = () => setExpandedId(expanded ? null : r.id)
              return (
                <Fragment key={r.id}>
                  <tr
                    onClick={toggle}
                    className={cn(
                      'border-t border-border-subtle align-top cursor-pointer transition',
                      expanded ? 'bg-bg-surface/60' : 'hover:bg-bg-surface/40',
                    )}
                  >
                    <td className="pl-2">
                      <ExpandToggle
                        expanded={expanded}
                        panelId={`vac-${r.id}`}
                        onToggle={toggle}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-3 py-3 text-text-primary">
                      <span className="inline-flex items-center gap-1.5">
                        {TYPE_LABEL[r.type]}
                        <PaymentBadge type={r.type} />
                      </span>
                    </td>
                    <td className="px-3 py-3 text-text-primary whitespace-nowrap">
                      {formatDateRange(r.startDate, r.endDate)}
                      <span className="block sm:hidden text-xs text-text-faint tabular-nums">
                        wpłynął {formatSubmittedShort(r.createdAt)}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-text-muted whitespace-nowrap tabular-nums hidden sm:table-cell">
                      {formatSubmittedShort(r.createdAt)}
                    </td>
                    <td className="px-3 py-3 text-right font-mono tabular-nums">
                      {r.workingDays}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {canCancel ? (
                        <Button
                          type="button"
                          variant="secondary"
                          className="px-3 py-1.5 text-xs"
                          onClick={(e) => {
                            e.stopPropagation()
                            onCancel(r)
                          }}
                        >
                          Anuluj
                        </Button>
                      ) : (
                        <span className="text-text-faint text-xs">—</span>
                      )}
                    </td>
                  </tr>
                  {expanded && (
                    <tr id={`vac-${r.id}`} className="bg-bg-surface/60">
                      <td colSpan={7} className="px-4 pb-4 pt-1">
                        <VacationRequestDetails request={r} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function CancelConfirmDialog({
  request,
  onClose,
}: {
  request: VacationRequest
  onClose: () => void
}) {
  const cancel = useCancelMyVacationRequest()

  async function onConfirm() {
    try {
      await cancel.mutateAsync(request.id)
      toast.success('Wniosek anulowany')
      onClose()
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-bg-canvas/80 backdrop-blur-sm p-4"
    >
      <div className="rounded-card border border-border-subtle bg-bg-elevated p-6 max-w-md w-full">
        <h2 className="text-lg font-semibold mb-2">Anulować wniosek?</h2>
        <p className="text-sm text-text-muted mb-4">
          {TYPE_LABEL[request.type]} — {formatDateRange(request.startDate, request.endDate)} ({request.workingDays} dni).
          {request.status === 'APPROVED' && ' Bilans urlopu zostanie przywrócony.'}
        </p>
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="secondary" onClick={onClose}>
            Wróć
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            loading={cancel.isPending}
            className="bg-danger text-bg-elevated hover:opacity-90"
          >
            Tak, anuluj
          </Button>
        </div>
      </div>
    </div>
  )
}
